import { useCallback, useEffect, useRef, type RefObject } from 'react';
import {
  Platform,
  type GestureResponderEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';

const TOP_REFRESH_THRESHOLD = 24;
const WEB_PULL_REFRESH_THRESHOLD = 72;
const SCROLL_RETRY_DELAYS_MS = [80, 180];

type ScrollToOffsetRef = {
  scrollToOffset: (params: { animated?: boolean; offset: number }) => void;
};

type RefreshableListInteractionOptions<TRef extends ScrollToOffsetRef> = {
  listRef: RefObject<TRef | null>;
  onRefresh: () => void;
  refreshing: boolean;
};

export function useRefreshableListInteractions<
  TRef extends ScrollToOffsetRef,
>({
  listRef,
  onRefresh,
  refreshing,
}: RefreshableListInteractionOptions<TRef>) {
  const scrollOffsetRef = useRef(0);
  const latestRef = useRef({ onRefresh, refreshing });
  const pullRef = useRef({
    active: false,
    maxDelta: 0,
    startY: 0,
  });

  useEffect(() => {
    latestRef.current = { onRefresh, refreshing };
  }, [onRefresh, refreshing]);

  const isAtTop = useCallback(
    () => scrollOffsetRef.current <= TOP_REFRESH_THRESHOLD,
    [],
  );

  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      scrollOffsetRef.current = Math.max(
        0,
        event.nativeEvent.contentOffset.y,
      );
    },
    [],
  );

  const refreshIfAtTop = useCallback(() => {
    const latest = latestRef.current;

    if (!isAtTop() || latest.refreshing) {
      return false;
    }

    latest.onRefresh();
    return true;
  }, [isAtTop]);

  const scrollToTop = useCallback(() => {
    if (refreshIfAtTop()) {
      return;
    }

    const scroll = () => {
      listRef.current?.scrollToOffset({ animated: true, offset: 0 });
    };

    scroll();
    SCROLL_RETRY_DELAYS_MS.forEach((delay) => {
      setTimeout(scroll, delay);
    });
  }, [listRef, refreshIfAtTop]);

  const resetPull = useCallback(() => {
    pullRef.current.active = false;
    pullRef.current.maxDelta = 0;
    pullRef.current.startY = 0;
  }, []);

  const onTouchStart = useCallback(
    (event: GestureResponderEvent) => {
      if (Platform.OS !== 'web') {
        return;
      }

      const latest = latestRef.current;

      if (latest.refreshing || !isAtTop()) {
        return;
      }

      pullRef.current.active = true;
      pullRef.current.maxDelta = 0;
      pullRef.current.startY = event.nativeEvent.pageY;
    },
    [isAtTop],
  );

  const onTouchMove = useCallback(
    (event: GestureResponderEvent) => {
      if (Platform.OS !== 'web' || !pullRef.current.active) {
        return;
      }

      const delta = event.nativeEvent.pageY - pullRef.current.startY;

      if (delta < 0) {
        resetPull();
        return;
      }

      pullRef.current.maxDelta = Math.max(pullRef.current.maxDelta, delta);
    },
    [resetPull],
  );

  const onTouchEnd = useCallback(() => {
    if (
      Platform.OS === 'web' &&
      pullRef.current.active &&
      pullRef.current.maxDelta >= WEB_PULL_REFRESH_THRESHOLD
    ) {
      const latest = latestRef.current;

      if (!latest.refreshing && isAtTop()) {
        latest.onRefresh();
      }
    }

    resetPull();
  }, [isAtTop, resetPull]);

  return {
    onScroll,
    scrollToTop,
    webPullToRefreshHandlers: {
      onTouchCancel: resetPull,
      onTouchEnd,
      onTouchMove,
      onTouchStart,
    },
  };
}
